import axios from 'axios';
import { Transformation } from 'cloudinary-core';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Media, MediaType, QualityOptions, Showroom } from 'generated/graphql';

import { RequiredFields } from 'Types/RequiredFields';

import { useCustomNotifications } from 'lib/Hooks';
import { getImageUrl } from 'lib/cloudinary';
import { useTrackApi } from 'lib/trackApi';

type AvailableQualityOption = {
	value: QualityOptions;
	label: string;
};

type HookReturnType = [AvailableQualityOption[], (option: QualityOptions) => void, { loading: boolean }];

export const useDownloadSingleMedia = (
	media: RequiredFields<Media, 'cloudinaryId' | 'name' | 'mediaType' | 'extension'>,
	showroom: Showroom
): HookReturnType => {
	const { t } = useTranslation();
	const { track } = useTrackApi('showroom', showroom.tenant.tenant, true);
	const [loading, setLoading] = React.useState(false);
	const { enqueueNotification, closeSnackbar } = useCustomNotifications();

	const options: AvailableQualityOption[] = React.useMemo(() => {
		return media?.mediaType === MediaType.Pdf
			? [{ value: QualityOptions.Screen, label: t('showroom.download.menu.subOptions.pdf') }]
			: [
					{ value: QualityOptions.Screen, label: t('showroom.download.menu.subOptions.screen') },
					{ value: QualityOptions.Original, label: t('showroom.download.menu.subOptions.hd') }
			  ];
	}, [t, media]);

	const downloadSingleMedia = React.useCallback(
		async (option: QualityOptions) => {
			const transformation: Transformation.Options[] = [];
			if (option === QualityOptions.Screen) transformation.push({ quality: 'auto:good' });

			closeSnackbar();

			const mediaUrl = getImageUrl(media.cloudinaryId, {
				format: media.extension,
				resource_type: media.mediaType === MediaType.Pdf ? 'image' : media.mediaType.toLowerCase(),
				transformation: [...transformation]
			});

			// Always append extension to filename to ensure the file is downloaded with the correct extension if the name has dots
			const fileName = `${media.name}.${media.extension}`;
			setLoading(true);
			try {
				const response = await axios.get(mediaUrl, { responseType: 'blob' });
				// @ts-ignore
				if (window.navigator && window.navigator.msSaveOrOpenBlob) {
					// IE variant
					// @ts-ignore
					window.navigator.msSaveOrOpenBlob(new Blob([response.data], { type: response.headers['content-type'] }), fileName);
				} else {
					const url = window.URL.createObjectURL(new Blob([response.data], { type: response.headers['content-type'] }));
					const link = document.createElement('a');
					link.href = url;
					link.setAttribute('download', fileName);
					document.body.appendChild(link);
					link.click();
					link.remove();
				}
				setLoading(false);
				track([
					{
						eventType: 'download',
						mediaType: media.mediaType.toLowerCase(),
						mediaId: media.cloudinaryId,
						showroomId: showroom.id,
						downloadQuality: option
					}
				]);
			} catch (error) {
				enqueueNotification(
					{
						title:
							error?.response?.status === 404
								? t('showroom.download.notification.single.error.notAvailable.title')
								: t('showroom.download.notification.single.error.generic.title'),
						text: (
							<span>
								{error?.response?.status === 404
									? t('showroom.download.notification.single.error.notAvailable.description')
									: t('showroom.download.notification.single.error.generic.description')}
							</span>
						)
					},
					{
						autoHideDuration: 7000,
						transitionDuration: {
							enter: 300
						}
					}
				);
				setLoading(false);
			}
		},
		[closeSnackbar, enqueueNotification, media, t, showroom, track]
	);

	return [options, downloadSingleMedia, { loading }];
};
